import React, { useEffect, useState } from "react";
import { Edit, Save, X, Plus, RotateCw, Eye, EyeOff } from "lucide-react";
import { encryptAesGcm } from "../services/crypto_aes";
import AdminCardActions from "../components/AdminCardActions";
import CollapsibleMobileCard from "../components/CollapsibleMobileCard";
import ToolbarSearch from "../components/ToolbarSearch";
import {
    fetchOperators,
    updateOperator,
    addOperator,
} from "../services/operators";
import { fetchActiveNetworks } from "../services/networks";
import { fetchServices } from "../services/services";
import { SECRET_KEY } from "../config/vars";

const phoneRegex = /^(\+?38)?0\d{9}$/;

const formatPhone = (phone) => {
    let digits = phone.replace(/\D/g, "");
    if (!digits.startsWith("38")) {
        digits = "38" + digits;
    }
    return "+" + digits;
};

const getServicesForNetwork = (services, networkName, selectedServiceId = null) => {
    const normalizedNetwork = (networkName || "").trim();
    const baseServices = services.filter(
        (service) => service.status && (service.network_name || "") === normalizedNetwork
    );

    if (!selectedServiceId) {
        return baseServices;
    }

    const selectedService = services.find((service) => service.id === selectedServiceId);
    if (!selectedService) {
        return baseServices;
    }

    return baseServices.some((service) => service.id === selectedServiceId)
        ? baseServices
        : [...baseServices, selectedService];
};

const OperatorsAdmins = () => {
    const [operators, setOperators] = useState([]);
    const [filteredOperators, setFilteredOperators] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("operator_name");
    const [sortDir, setSortDir] = useState("asc");
    const [networks, setNetworks] = useState([]);
    const [services, setServices] = useState([]);
    const [editingOperatorId, setEditingOperatorId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [editedData, setEditedData] = useState({});
    const [originalData, setOriginalData] = useState({});
    const [newOperator, setNewOperator] = useState({
        operator_name: "",
        email: "",
        phone: "",
        password_hash: "",
        descriptions: "",
        status: true,
        network_name: "",
        service_id: null,
        can_see_clients: false,
        mfa_enabled: true,
    });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showNewOperatorPassword, setShowNewOperatorPassword] = useState(false);
    const [showEditedOperatorPassword, setShowEditedOperatorPassword] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        loadData();

        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const q = searchQuery.toLowerCase();
        const filtered = operators.filter((op) =>
            (op.operator_name || "").toString().toLowerCase().includes(q) ||
            (op.email || "").toString().toLowerCase().includes(q) ||
            (op.phone || "").toString().toLowerCase().includes(q) ||
            (op.service_name || "").toString().toLowerCase().includes(q)
        );
        setFilteredOperators(filtered);
    }, [operators, searchQuery]);

    const loadData = async () => {
        const [ops, nets, servs] = await Promise.all([fetchOperators(), fetchActiveNetworks(), fetchServices()]);
        setOperators(ops);
        setNetworks(nets);
        setServices(servs);
    };

    const handleEdit = (operator) => {
        setEditingOperatorId(operator.id);
        setExpandedId(operator.id);
        setEditedData({
            operator_name: operator.operator_name,
            email: operator.email,
            phone: operator.phone,
            password_hash: "",
            descriptions: operator.descriptions,
            status: operator.status,
            network_name: operator.network_name || "",
            service_id: operator.service_id || null,
            can_see_clients: operator.can_see_clients,
            mfa_enabled: operator.mfa_enabled,
        });
        setOriginalData({
            operator_name: operator.operator_name,
            email: operator.email,
            phone: operator.phone,
            password_hash: "",
            descriptions: operator.descriptions,
            status: operator.status,
            network_name: operator.network_name || "",
            service_id: operator.service_id || null,
            can_see_clients: operator.can_see_clients,
            mfa_enabled: operator.mfa_enabled,
        });
    };

    const handleCancelEdit = () => {
        setEditedData(originalData);
        setEditingOperatorId(null);
    };

    const handleSave = async (id) => {
        if (!SECRET_KEY) {
            alert("Не налаштований ключ шифрування (REACT_APP_SECRET_KEY)");
            return;
        }

        const updatedData = { ...editedData };

        if (!phoneRegex.test(updatedData.phone)) {
            alert("Неправильний формат телефону");
            return;
        }

        updatedData.phone = formatPhone(updatedData.phone);

        // Якщо пароль не змінений, не відправляємо його
        if (!editedData.password_hash || !editedData.password_hash.trim()) {
            updatedData.password_hash = null;
        } else {
            updatedData.password_hash = await encryptAesGcm(SECRET_KEY, editedData.password_hash);
        }

        await updateOperator(id, updatedData);
        await loadData();
        setEditingOperatorId(null);
    };

    const handleAddOperator = async () => {
        if (!SECRET_KEY) {
            alert("Не налаштований ключ шифрування (REACT_APP_SECRET_KEY)");
            return;
        }

        if (!newOperator.operator_name.trim() || !newOperator.email.trim()) {
            alert("Ім'я та email не можуть бути порожніми!");
            return;
        }

        if (!phoneRegex.test(newOperator.phone)) {
            alert("Неправильний формат телефону");
            return;
        }

        if (!newOperator.password_hash || !newOperator.password_hash.trim()) {
            alert("Пароль не може бути порожнім");
            return;
        }

        const encryptedPassword = await encryptAesGcm(SECRET_KEY, newOperator.password_hash);
        const operatorData = {
            ...newOperator,
            phone: formatPhone(newOperator.phone),
            password_hash: encryptedPassword,
        }; await addOperator(operatorData);

        setNewOperator({
            operator_name: "",
            email: "",
            phone: "",
            password_hash: "",
            descriptions: "",
            status: true,
            network_name: "",
            service_id: null,
            can_see_clients: false,
            mfa_enabled: true,
        });

        setShowAddModal(false);
        await loadData();
    };

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        if (!filteredOperators) return [];
        const sorted = [...filteredOperators];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";
            // Конвертуємо все в рядок для порівняння, включаючи boolean
            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            } else {
                return valA < valB ? 1 : valA > valB ? -1 : 0;
            }
        });
        return sorted;
    };

    return (
        <div>
            <div className="config-header">
                <div className="header-buttons">
                    <button className="add-btn toolbar-icon-btn" onClick={() => setShowAddModal(true)} title="Додати" aria-label="Додати">
                        <Plus size={16} />
                    </button>
                    <button className="add-btn refresh toolbar-icon-btn" onClick={loadData} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} />
                    </button>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук за ім'ям, email або телефоном..."
                    />
                </div>
            </div>

            {showAddModal && (
                <div className="modal-backdrop" onClick={() => setShowAddModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Додати оператора</h3>

                        {["operator_name", "email", "phone", "descriptions"].map((field) => (
                            <input
                                key={field}
                                type="text"
                                placeholder={field.replace("_", " ")}
                                value={newOperator[field]}
                                onChange={(e) => setNewOperator({ ...newOperator, [field]: e.target.value })}
                            />
                        ))}

                        <div className="password-input-wrapper">
                            <input
                                type={showNewOperatorPassword ? "text" : "password"}
                                placeholder="Пароль"
                                value={newOperator.password_hash}
                                onChange={(e) => setNewOperator({ ...newOperator, password_hash: e.target.value })}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                className="toggle-password-btn"
                                onClick={() => setShowNewOperatorPassword((v) => !v)}
                                title={showNewOperatorPassword ? "Приховати пароль" : "Показати пароль"}
                                aria-label={showNewOperatorPassword ? "Приховати пароль" : "Показати пароль"}
                            >
                                {showNewOperatorPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>

                        <select
                            value={newOperator.network_name}
                            onChange={(e) => setNewOperator({ ...newOperator, network_name: e.target.value, service_id: null })}
                        >
                            <option value="">Оберіть мережу</option>
                            {networks.filter((n) => n.status).map((n) => (
                                <option key={n.id} value={n.network_name}>
                                    {n.network_name}
                                </option>
                            ))}
                        </select>

                        <select
                            value={newOperator.service_id || ""}
                            onChange={(e) => setNewOperator({ ...newOperator, service_id: e.target.value ? parseInt(e.target.value) : null })}
                            disabled={!newOperator.network_name}
                        >
                            <option value="">{newOperator.network_name ? "Оберіть сервіс" : "Спочатку оберіть мережу"}</option>
                            {getServicesForNetwork(services, newOperator.network_name).map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.name}
                                </option>
                            ))}
                        </select>

                        <select
                            value={newOperator.status}
                            onChange={(e) => setNewOperator({ ...newOperator, status: e.target.value === "true" })}
                        >
                            <option value="true">Активний</option>
                            <option value="false">Неактивний</option>
                        </select>

                        <div className="checkbox-group">
                            <label className="modal-checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={newOperator.can_see_clients}
                                    onChange={(e) =>
                                        setNewOperator({ ...newOperator, can_see_clients: e.target.checked })
                                    }
                                />
                                <span>Може бачити клієнтів</span>
                            </label>

                            <label className="modal-checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={newOperator.mfa_enabled}
                                    onChange={(e) =>
                                        setNewOperator({ ...newOperator, mfa_enabled: e.target.checked })
                                    }
                                />
                                <span>MFA увімкнено</span>
                            </label>
                        </div>

                        <div className="modal-actions">
                            <button className="add-btn" onClick={handleAddOperator}>Додати</button>
                            <button onClick={() => setShowAddModal(false)} className="add-btn cancel-btn">Скасувати</button>
                        </div>
                    </div>
                </div>
            )}

            {isMobile ? (
                <div className="config-grid">
                    {filteredOperators.map((operator) => (
                        <CollapsibleMobileCard
                            key={operator.id}
                            expanded={expandedId === operator.id}
                            editing={editingOperatorId === operator.id}
                            onToggle={() => setExpandedId((cur) => (cur === operator.id ? null : operator.id))}
                            title={operator.operator_name}
                            meta={<span>{operator.status ? "Активний" : "Неактивний"}</span>}
                            preview={[operator.email, operator.network_name || operator.service_name].filter(Boolean).join(" · ")}
                            actions={(
                                <AdminCardActions
                                    editing={editingOperatorId === operator.id}
                                    onEdit={() => handleEdit(operator)}
                                    onSave={() => handleSave(operator.id)}
                                    onCancel={handleCancelEdit}
                                />
                            )}
                        >
                            <div>
                                <span className="collapsible-card__field-label">Ім’я</span>
                                {editingOperatorId === operator.id ? (
                                    <input value={editedData.operator_name} onChange={(e) => setEditedData({ ...editedData, operator_name: e.target.value })} />
                                ) : operator.operator_name}
                            </div>
                            <div>
                                <span className="collapsible-card__field-label">Email</span>
                                {editingOperatorId === operator.id ? (
                                    <input value={editedData.email} onChange={(e) => setEditedData({ ...editedData, email: e.target.value })} />
                                ) : operator.email}
                            </div>
                            <div>
                                <span className="collapsible-card__field-label">Пароль</span>
                                {editingOperatorId === operator.id ? (
                                    <div className="password-input-wrapper">
                                        <input
                                            type={showEditedOperatorPassword ? "text" : "password"}
                                            value={editedData.password_hash}
                                            onChange={(e) => setEditedData({ ...editedData, password_hash: e.target.value })}
                                            autoComplete="new-password"
                                        />
                                        <button
                                            type="button"
                                            className="toggle-password-btn"
                                            onClick={() => setShowEditedOperatorPassword((v) => !v)}
                                            title={showEditedOperatorPassword ? "Приховати пароль" : "Показати пароль"}
                                            aria-label={showEditedOperatorPassword ? "Приховати пароль" : "Показати пароль"}
                                        >
                                            {showEditedOperatorPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                ) : "••••••"}
                            </div>
                            <div>
                                <span className="collapsible-card__field-label">Телефон</span>
                                {editingOperatorId === operator.id ? (
                                    <input value={editedData.phone} onChange={(e) => setEditedData({ ...editedData, phone: e.target.value })} />
                                ) : operator.phone}
                            </div>
                            <div>
                                <span className="collapsible-card__field-label">Опис</span>
                                {editingOperatorId === operator.id ? (
                                    <input value={editedData.descriptions} onChange={(e) => setEditedData({ ...editedData, descriptions: e.target.value })} />
                                ) : (operator.descriptions || "(Немає)")}
                            </div>
                            <div>
                                <span className="collapsible-card__field-label">Статус</span>
                                {editingOperatorId === operator.id ? (
                                    <select value={editedData.status} onChange={(e) => setEditedData({ ...editedData, status: e.target.value === "true" })}>
                                        <option value="true">Активний</option>
                                        <option value="false">Неактивний</option>
                                    </select>
                                ) : (operator.status ? "Активний" : "Неактивний")}
                            </div>
                            <div>
                                <span className="collapsible-card__field-label">Мережа</span>
                                {editingOperatorId === operator.id ? (
                                    <select value={editedData.network_name} onChange={(e) => setEditedData({ ...editedData, network_name: e.target.value, service_id: null })}>
                                        <option value="">(Не прив'язано)</option>
                                        {networks.filter((n) => n.status).map((n) => (
                                            <option key={n.id} value={n.network_name}>{n.network_name}</option>
                                        ))}
                                    </select>
                                ) : (operator.network_name || "(Немає)")}
                            </div>
                            <div>
                                <span className="collapsible-card__field-label">Сервіс</span>
                                {editingOperatorId === operator.id ? (
                                    <select
                                        value={editedData.service_id || ""}
                                        onChange={(e) => setEditedData({ ...editedData, service_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                                        disabled={!editedData.network_name}
                                    >
                                        <option value="">{editedData.network_name ? "Оберіть сервіс" : "Спочатку оберіть мережу"}</option>
                                        {getServicesForNetwork(services, editedData.network_name, editedData.service_id).map((s) => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                ) : (operator.service_name || "(Немає)")}
                            </div>
                            <div className="checkbox-inline">
                                <span className="collapsible-card__field-label">Перегляд клієнтів</span>
                                {editingOperatorId === operator.id ? (
                                    <input
                                        type="checkbox"
                                        checked={editedData.can_see_clients}
                                        onChange={(e) =>
                                            setEditedData({ ...editedData, can_see_clients: e.target.checked })
                                        }
                                    />
                                ) : (operator.can_see_clients ? "✅ Так" : "❌ Ні")}
                            </div>
                            <div className="checkbox-inline">
                                <span className="collapsible-card__field-label">MFA увімкнено</span>
                                {editingOperatorId === operator.id ? (
                                    <input
                                        type="checkbox"
                                        checked={editedData.mfa_enabled}
                                        onChange={(e) =>
                                            setEditedData({ ...editedData, mfa_enabled: e.target.checked })
                                        }
                                    />
                                ) : (operator.mfa_enabled ? "✅ Так" : "❌ Ні")}
                            </div>
                        </CollapsibleMobileCard>
                    ))}
                </div>
            ) : (
                <div className="table-responsive">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("operator_name")}>
                                    Ім'я {sortBy === "operator_name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("email")}>
                                    Email {sortBy === "email" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th>Пароль</th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("phone")}>
                                    Телефон {sortBy === "phone" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("descriptions")}>
                                    Опис {sortBy === "descriptions" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>
                                    Статус {sortBy === "status" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("network_name")}>
                                    Мережа {sortBy === "network_name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("service_name")}>
                                    Сервіс {sortBy === "service_name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("can_see_clients")}>
                                    Клієнти {sortBy === "can_see_clients" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("mfa_enabled")}>
                                    МФА {sortBy === "mfa_enabled" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedData().map((operator) => (
                                <tr key={operator.id}>
                                    {[
                                        "operator_name",
                                        "email",
                                        "password_hash",
                                        "phone",
                                        "descriptions",
                                        "status",
                                        "network_name",
                                        "service_id",
                                        "can_see_clients",
                                        "mfa_enabled",
                                    ].map((field, i) => (
                                        <td key={i}>
                                            {editingOperatorId === operator.id ? (
                                                field === "status" ? (
                                                    <select
                                                        value={editedData.status}
                                                        onChange={(e) =>
                                                            setEditedData({
                                                                ...editedData,
                                                                status: e.target.value === "true",
                                                            })
                                                        }
                                                    >
                                                        <option value="true">Активний</option>
                                                        <option value="false">Неактивний</option>
                                                    </select>
                                                ) : field === "network_name" ? (
                                                    <select
                                                        value={editedData.network_name}
                                                        onChange={(e) =>
                                                            setEditedData({
                                                                ...editedData,
                                                                network_name: e.target.value,
                                                                service_id: null,
                                                            })
                                                        }
                                                    >
                                                        <option value="">(Не прив'язано)</option>
                                                        {networks.filter((n) => n.status).map((n) => (
                                                            <option key={n.id} value={n.network_name}>
                                                                {n.network_name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : field === "service_id" ? (
                                                    <select
                                                        value={editedData.service_id}
                                                        disabled={!editedData.network_name}
                                                        onChange={(e) =>
                                                            setEditedData({
                                                                ...editedData,
                                                                service_id: e.target.value ? parseInt(e.target.value) : null,
                                                            })
                                                        }
                                                    >
                                                        <option value="">{editedData.network_name ? "Оберіть сервіс" : "Спочатку оберіть мережу"}</option>
                                                        {getServicesForNetwork(services, editedData.network_name, editedData.service_id).map((s) => (
                                                            <option key={s.id} value={s.id}>
                                                                {s.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : field === "can_see_clients" ? (
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            checked={editedData.can_see_clients}
                                                            onChange={(e) =>
                                                                setEditedData({
                                                                    ...editedData,
                                                                    can_see_clients: e.target.checked,
                                                                })
                                                            }
                                                        />
                                                    </label>
                                                ) : field === "mfa_enabled" ? (
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            checked={editedData.mfa_enabled}
                                                            onChange={(e) =>
                                                                setEditedData({
                                                                    ...editedData,
                                                                    mfa_enabled: e.target.checked,
                                                                })
                                                            }
                                                        />
                                                    </label>
                                                ) : field === "password_hash" ? (
                                                    <div className="password-input-wrapper">
                                                        <input
                                                            type={showEditedOperatorPassword ? "text" : "password"}
                                                            value={editedData.password_hash}
                                                            onChange={(e) =>
                                                                setEditedData({
                                                                    ...editedData,
                                                                    password_hash: e.target.value,
                                                                })
                                                            }
                                                            placeholder="Залишити порожнім щоб не змінювати"
                                                            autoComplete="new-password"
                                                        />
                                                        <button
                                                            type="button"
                                                            className="toggle-password-btn"
                                                            onClick={() => setShowEditedOperatorPassword((v) => !v)}
                                                            title={showEditedOperatorPassword ? "Приховати пароль" : "Показати пароль"}
                                                            aria-label={showEditedOperatorPassword ? "Приховати пароль" : "Показати пароль"}
                                                        >
                                                            {showEditedOperatorPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={editedData[field]}
                                                        onChange={(e) =>
                                                            setEditedData({
                                                                ...editedData,
                                                                [field]: e.target.value,
                                                            })
                                                        }
                                                    />
                                                )
                                            ) : field === "password_hash" ? (
                                                "••••••"
                                            ) : field === "status" ? (
                                                operator.status ? "Активний" : "Неактивний"
                                            ) : field === "can_see_clients" ? (
                                                operator.can_see_clients ? "Так" : "Ні"
                                            ) : field === "mfa_enabled" ? (
                                                operator.mfa_enabled ? "Так" : "Ні"
                                            ) : field === "service_id" ? (
                                                operator.service_name || "(Немає)"
                                            ) : (
                                                operator[field] || "(Немає)"
                                            )}
                                        </td>
                                    ))}
                                    <td className="actions">
                                        {editingOperatorId === operator.id ? (
                                            <>
                                                <button
                                                    className="icon-button"
                                                    onClick={() => handleSave(operator.id)}
                                                    title="Зберегти"
                                                >
                                                    <Save className="icon-btn" />
                                                </button>
                                                <button
                                                    className="icon-button"
                                                    onClick={handleCancelEdit}
                                                    title="Скасувати"
                                                >
                                                    <X className="icon-btn" />
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                className="icon-button"
                                                onClick={() => handleEdit(operator)}
                                                title="Редагувати"
                                            >
                                                <Edit className="icon-btn" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default OperatorsAdmins;
